import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Operator, Supervisor, CreateOperatorRequest, UpdateOperatorRequest } from '../../services/operatorsApi';

// State interfaces
export interface OperatorsState {
  // Data
  operators: Operator[];
  selectedOperator: Operator | null;
  supervisors: Supervisor[];
  
  // UI State
  loading: boolean;
  error: string | null;
  
  // Form State
  isEditing: boolean;
  isDialogOpen: boolean;
  validationErrors: string[];
  
  // List State
  searchTerm: string;
  filters: {
    active: string; // 'all', 'true', 'false'
    supervisorId: number | null;
  };
  pagination: {
    page: number;
    pageSize: number;
    total: number;
  };
  
  // Statistics
  statistics: {
    total: number;
    active: number;
    inactive: number;
  };
  
  // Bulk Operations
  selectedOperatorIds: number[];
  bulkLoading: boolean;
}

const initialState: OperatorsState = {
  operators: [],
  selectedOperator: null,
  supervisors: [],
  loading: false,
  error: null,
  isEditing: false,
  isDialogOpen: false,
  validationErrors: [],
  searchTerm: '',
  filters: {
    active: 'all',
    supervisorId: null,
  },
  pagination: {
    page: 1,
    pageSize: 25,
    total: 0,
  },
  statistics: {
    total: 0,
    active: 0,
    inactive: 0,
  },
  selectedOperatorIds: [],
  bulkLoading: false,
};

const operatorsSlice = createSlice({
  name: 'operators',
  initialState,
  reducers: {
    // Loading states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setBulkLoading: (state, action: PayloadAction<boolean>) => {
      state.bulkLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    
    // Data actions
    setOperators: (state, action: PayloadAction<Operator[]>) => {
      state.operators = action.payload;
      state.pagination.total = action.payload.length;
    },
    setSelectedOperator: (state, action: PayloadAction<Operator | null>) => {
      state.selectedOperator = action.payload;
    },
    setSupervisors: (state, action: PayloadAction<Supervisor[]>) => {
      state.supervisors = action.payload;
    },
    addOperator: (state, action: PayloadAction<Operator>) => {
      state.operators.push(action.payload);
      state.pagination.total += 1;
    },
    updateOperator: (state, action: PayloadAction<Operator>) => {
      const index = state.operators.findIndex(op => op.Id === action.payload.Id);
      if (index !== -1) {
        state.operators[index] = action.payload;
      }
    },
    removeOperator: (state, action: PayloadAction<number>) => {
      state.operators = state.operators.filter(op => op.Id !== action.payload);
      state.pagination.total -= 1;
    },
    
    // Dialog and form actions
    setDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isDialogOpen = action.payload;
      if (!action.payload) {
        state.selectedOperator = null;
        state.isEditing = false;
        state.validationErrors = [];
      }
    },
    setEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },
    setValidationErrors: (state, action: PayloadAction<string[]>) => {
      state.validationErrors = action.payload;
    },
    
    // Search and filters
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
      state.pagination.page = 1; // Reset to first page when searching
    },
    setFilters: (state, action: PayloadAction<Partial<OperatorsState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.page = 1; // Reset to first page when filtering
    },
    
    // Pagination
    setPagination: (state, action: PayloadAction<Partial<OperatorsState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    
    // Statistics
    setStatistics: (state, action: PayloadAction<OperatorsState['statistics']>) => {
      state.statistics = action.payload;
    },
    
    // Bulk operations
    setSelectedOperatorIds: (state, action: PayloadAction<number[]>) => {
      state.selectedOperatorIds = action.payload;
    },
    toggleOperatorSelection: (state, action: PayloadAction<number>) => {
      const operatorId = action.payload;
      const isSelected = state.selectedOperatorIds.includes(operatorId);
      if (isSelected) {
        state.selectedOperatorIds = state.selectedOperatorIds.filter(id => id !== operatorId);
      } else {
        state.selectedOperatorIds.push(operatorId);
      }
    },
    selectAllOperators: (state) => {
      state.selectedOperatorIds = state.operators.map(op => op.Id);
    },
    deselectAllOperators: (state) => {
      state.selectedOperatorIds = [];
    },
    
    // Reset state
    resetState: () => initialState,
  },
});

// Action creators
export const {
  setLoading,
  setBulkLoading,
  setError,
  setOperators,
  setSelectedOperator,
  setSupervisors,
  addOperator,
  updateOperator,
  removeOperator,
  setDialogOpen,
  setEditing,
  setValidationErrors,
  setSearchTerm,
  setFilters,
  setPagination,
  setStatistics,
  setSelectedOperatorIds,
  toggleOperatorSelection,
  selectAllOperators,
  deselectAllOperators,
  resetState,
} = operatorsSlice.actions;

// Saga action types
export const FETCH_OPERATORS = 'operators/fetchOperators';
export const FETCH_OPERATOR_BY_ID = 'operators/fetchOperatorById';
export const CREATE_OPERATOR = 'operators/createOperator';
export const UPDATE_OPERATOR = 'operators/updateOperator';
export const DELETE_OPERATOR = 'operators/deleteOperator';
export const FETCH_SUPERVISORS = 'operators/fetchSupervisors';
export const BULK_ACTIVATE_OPERATORS = 'operators/bulkActivateOperators';
export const BULK_DEACTIVATE_OPERATORS = 'operators/bulkDeactivateOperators';
export const FETCH_OPERATORS_STATISTICS = 'operators/fetchOperatorsStatistics';

// Saga action creators
export const fetchOperators = () => ({ type: FETCH_OPERATORS });
export const fetchOperatorById = (id: number) => ({ type: FETCH_OPERATOR_BY_ID, payload: id });
export const createOperator = (operator: CreateOperatorRequest) => ({ type: CREATE_OPERATOR, payload: operator });
export const updateOperatorAction = (operator: UpdateOperatorRequest) => ({ type: UPDATE_OPERATOR, payload: operator });
export const deleteOperator = (id: number) => ({ type: DELETE_OPERATOR, payload: id });
export const fetchSupervisors = () => ({ type: FETCH_SUPERVISORS });
export const bulkActivateOperators = (ids: number[]) => ({ type: BULK_ACTIVATE_OPERATORS, payload: ids });
export const bulkDeactivateOperators = (ids: number[]) => ({ type: BULK_DEACTIVATE_OPERATORS, payload: ids });
export const fetchOperatorsStatistics = () => ({ type: FETCH_OPERATORS_STATISTICS });

export default operatorsSlice.reducer; 